import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SpecIdWithMaterialIds } from '@shared/models/rx-rules-json-interface';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { MaterialOption } from '@shared/models/material-option';
import { MaterialToothEditorRule } from '@shared/models/material-tooth-editor-rule';
import { IdName } from '@shared/models/id-name';
import { RxGeneralRules } from '@shared/models/rx-general-rules';
import { UNIT_TYPE_MATERIAL_TOOTH_EDITOR_RULES, RX_GENERAL_RULES } from '@shared/rules/rules-tokens';
import { ProcedureRulesParser } from '@shared/services/rx-rules-helper/procedure-rules.parser';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import {
	PROCEDURE_IN_BRIDGE_RULES_PARSER,
	PROCEDURE_RULES_PARSER,
	PROCEDURE_SPECIFICATION_MATERIAL_BRIDGE_PARSER,
	PROCEDURE_SPECIFICATION_MATERIAL_PARSER,
	PROCEDURE_TOOTH_EDITOR_PARSER
} from '@shared/services/rx-rules-helper/parser-tokens';
import { ProcedureSpecificationMaterialParser } from '@shared/services/rx-rules-helper/procedure-specification-material.parser';
import { MaterialQuery } from '@modules/material/state/material-query';
import { LoggerService } from '@core/services/logger/logger.service';
import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { Specification } from '@shared/models/specification';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { decodeCustomMaterialEntityId, encodeCustomMaterialId, encodeCustomSpecificationId } from '@shared/utils/material-util';
import { isFixedRestorativeRestoLab } from '@shared/utils/send-to-utils';
import { Material } from '@shared/models/material';
import { getDefaultMaterialOptions } from '@shared/rules/custom-spec-material-tooth-editor-defaults';
import { ProcedureToothEditorParser } from './procedure-tooth-editor-parser';
import { CustomMaterial } from '@shared/models/custom-material';
import { ProcedureMapCheckedValue, procedureMapComposites } from '@core/procedure-helpers/models/procedure-map-composites';
import { Maybe } from '@/types/globals';

@Injectable({ providedIn: 'root' })
export class RxMaterialRulesService {
	private marginDesignOptionsTranslated: IdName[];
	private preparationDesignOptionsMappingTranslated: IdName[];

	constructor(
		@Inject(UNIT_TYPE_MATERIAL_TOOTH_EDITOR_RULES) private readonly unitTypeMaterialToothEditorRules: MaterialToothEditorRule[],
		@Inject(PROCEDURE_RULES_PARSER) private readonly procedureRulesParser: ProcedureRulesParser<UnitTypes>,
		@Inject(PROCEDURE_IN_BRIDGE_RULES_PARSER) private readonly procedureInBridgeRulesParser: ProcedureRulesParser<UnitTypesInBridge>,
		@Inject(PROCEDURE_SPECIFICATION_MATERIAL_PARSER)
		private readonly procedureSpecificationMaterialParser: ProcedureSpecificationMaterialParser<UnitTypes>,
		@Inject(PROCEDURE_SPECIFICATION_MATERIAL_BRIDGE_PARSER)
		private readonly procedureSpecificationMaterialBridgeParser: ProcedureSpecificationMaterialParser<UnitTypesInBridge>,
		@Inject(PROCEDURE_TOOTH_EDITOR_PARSER)
		private readonly procedureToothEditorParser: ProcedureToothEditorParser,
		@Inject(RX_GENERAL_RULES) private readonly rxGeneralRules: RxGeneralRules,
		private readonly materialQuery: MaterialQuery,
		private readonly logger: LoggerService,
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly translateService: TranslateService
	) {
		this.marginDesignOptionsTranslated = [...this.rxGeneralRules.ToothEditorRules.Crown.MarginDesigns];
		this.preparationDesignOptionsMappingTranslated = [...this.rxGeneralRules.IdNameMapping.PreparationDesign];
	}

	getMarginDesignOptions(): IdName[] {
		return this.marginDesignOptionsTranslated;
	}

	getPreparationDesignOptionsMapping(): IdName[] {
		return this.preparationDesignOptionsMappingTranslated;
	}

	translateCrownDesignSelectOptions(): void {
		this.marginDesignOptionsTranslated = this.rxGeneralRules.ToothEditorRules.Crown.MarginDesigns.map(marginDesign =>
			this.getTranslatedCrownMappingLabel(marginDesign, 'MarginDesign')
		);
		this.preparationDesignOptionsMappingTranslated = this.rxGeneralRules.IdNameMapping.PreparationDesign.map(preparationDesign =>
			this.getTranslatedCrownMappingLabel(preparationDesign, 'PreparationDesign')
		);
	}

	enrichMaterialRules(labId: number): void {
		const defaultSpecRules = this.procedureSpecificationMaterialParser.getDefaultRules();
		const defaultBridgeSpecRules = this.procedureSpecificationMaterialBridgeParser.getDefaultRules();

		try {
			const newRules = defaultSpecRules?.map(rule => {
				if (rule.ProcedureMaps.includes(procedureMapComposites.fixedRestorativeAll)) {
					return this.materialQuery.getAsMergedSpecificationMaterialRule(labId, rule, false);
				}

				return rule;
			});

			this.procedureSpecificationMaterialParser.setRules(newRules);
		} catch (error) {
			this.procedureSpecificationMaterialParser.setRules(defaultSpecRules);
			this.logger.error('Error enriching material rules', error);
		}

		try {
			const newRules = defaultBridgeSpecRules?.map(rule => {
				if (rule.ProcedureMaps.includes(procedureMapComposites.fixedRestorativeAll)) {
					return this.materialQuery.getAsMergedSpecificationMaterialRule(labId, rule, true);
				}

				return rule;
			});

			this.procedureSpecificationMaterialBridgeParser.setRules(newRules);
		} catch (error) {
			this.procedureSpecificationMaterialBridgeParser.setRules(defaultBridgeSpecRules);
			this.logger.error('Error enriching material rules', error);
		}
	}

	resetMaterialRules(): void {
		this.procedureSpecificationMaterialParser.setRules(this.procedureSpecificationMaterialParser.getDefaultRules());
		this.procedureSpecificationMaterialBridgeParser.setRules(this.procedureSpecificationMaterialBridgeParser.getDefaultRules());
	}

	getSpecIdsWithMaterialIds(pmToCheck: Maybe<ProcedureMapCheckedValue>, currentUnitTypeOnTooth: UnitTypes): SpecIdWithMaterialIds[] {
		return this.procedureSpecificationMaterialParser.getSpecIdsWithMaterialIds(pmToCheck, currentUnitTypeOnTooth);
	}

	getSpecIdsWithMaterialIdsForBridge(
		pmToCheck: Maybe<ProcedureMapCheckedValue>,
		bridgeUnitTypeOnTooth: UnitTypesInBridge
	): SpecIdWithMaterialIds[] {
		return this.procedureSpecificationMaterialBridgeParser.getSpecIdsWithMaterialIds(pmToCheck, bridgeUnitTypeOnTooth);
	}

	getMaterialUnitTypeOption({
		materialId,
		unitTypeId
	}: {
		materialId: number | string;
		unitTypeId: number | string;
	}): MaterialOption | undefined {
		const allMaterialOptions = this.unitTypeMaterialToothEditorRules.find(materialObj => materialObj.Id === materialId)?.Options;

		return allMaterialOptions?.find(materialOption => materialOption.UnitType === unitTypeId);
	}

	getMaterialIdsBySpecification(specificationId: number, specIdsWithMaterialIds: SpecIdWithMaterialIds[]): (number | string)[] {
		if (specIdsWithMaterialIds[0]?.Id === (SpecificationEnum.None as number)) {
			return specIdsWithMaterialIds[0].Materials;
		}

		return specIdsWithMaterialIds?.find(({ Id }) => Id === specificationId)?.Materials ?? [];
	}

	getFilteredCustomSpecifications(specIds: (number | string)[]): Specification[] {
		const sendTo = this.orderQuery.sendTo;
		const customSpecIds = specIds
			.map(specId => decodeCustomMaterialEntityId(specId))
			.filter(customSpecId => customSpecId.id !== undefined && customSpecId.labId !== undefined);

		if (isFixedRestorativeRestoLab(this.orderQuery.procedureMap, sendTo) && customSpecIds.length > 0) {
			return specIds
				.map(specId => this.materialQuery.getValue().specs[specId])
				.filter(spec => spec?.isAvailable)
				.map(
					spec =>
						({
							...spec,
							Id: encodeCustomSpecificationId(spec),
							Name: spec.name,
							ImplantBased:
								spec.indicationId === (UnitTypes.ImplantBased as number) ||
								spec.bridgeIndicationId === (UnitTypesInBridge.ImplantBased as number)
						}) satisfies Specification
				);
		}

		return [];
	}

	getFilteredSpecifications(specIds: (number | string)[], specifications?: Specification[]): Specification[] {
		if (!specifications) {
			specifications = [...(this.shellQuery.specifications ?? []), ...this.getFilteredCustomSpecifications(specIds)];
		}

		return specifications.filter(({ Id }) => specIds.includes(Id));
	}

	getSpecification(specId: number | string, specifications?: Specification[]): Specification {
		return this.getFilteredSpecifications([specId], specifications)?.[0];
	}

	getSpecificationName(specId: number | string): string {
		const specification = this.getSpecification(specId);

		return specification?.Name || '';
	}

	getMaterial(matId: number | string, materials?: Material[]): Material {
		return this.getFilteredMaterials([matId], materials)?.[0];
	}

	getMaterialName(materialId: number | string): string {
		const material = this.getMaterial(materialId);

		return material?.Name || '';
	}

	getFilteredCustomMaterials(materialIds: (number | string)[]): Material[] {
		const sendTo = this.orderQuery.sendTo;

		if (isFixedRestorativeRestoLab(this.orderQuery.procedureMap, sendTo) && materialIds?.length > 0) {
			return materialIds
				.map(materialId => this.materialQuery.getValue().materials[materialId])
				.filter(customMaterial => customMaterial?.isAvailable)
				.map(customMaterial => {
					const iTeroMaterialOrDefaultOptions =
						this.shellQuery.materials?.find(im => im.Id === customMaterial.iTeroMaterialId) ??
						({
							Options: getDefaultMaterialOptions(customMaterial.materialGroup),
							AllowCustomAbutment: false,
							Group: 0
						} satisfies Pick<Material, 'Options' | 'AllowCustomAbutment' | 'Group'>);

					return {
						...customMaterial,
						Id: encodeCustomMaterialId(customMaterial),
						Name: customMaterial.name,
						IsActive: customMaterial.isAvailable,
						Options: iTeroMaterialOrDefaultOptions.Options,
						AllowCustomAbutment: iTeroMaterialOrDefaultOptions.AllowCustomAbutment,
						Group: iTeroMaterialOrDefaultOptions.Group
					} satisfies Material;
				});
		}

		return [];
	}

	getFilteredMaterials(materialIds: (number | string)[], materialNames?: Material[]): Material[] {
		if (!materialIds || materialIds.length === 0) {
			return [];
		}

		if (!materialNames) {
			materialNames = [...(this.shellQuery.materials ?? []), ...this.getFilteredCustomMaterials(materialIds)];
		}

		return materialNames.filter((materialNameItem: Material) => materialIds.includes(materialNameItem.Id) && materialNameItem.IsActive);
	}

	getMaterialSpecOption(
		materialId: number | string,
		specId: number | string | undefined,
		materialGroup?: string
	): MaterialOption | undefined {
		if (typeof materialId !== 'string' && typeof specId !== 'string') {
			// both material and spec are iTero non-custom
			return this.procedureToothEditorParser.getMaterialSpecOption(materialId, specId ?? 0);
		}

		// custom material or spec
		let iTeroMaterialId: number | undefined;
		let iTeroSpecId: number | undefined;
		let customMaterial: CustomMaterial | undefined;

		if (typeof materialId === 'string') {
			// custom material
			// the material might have been removed or disabled. Default to trying to find it without a revision check.
			customMaterial =
				this.materialQuery.getValue().materials[materialId] ?? this.materialQuery.getMaterialWithoutRevisionCheck(materialId);

			// try to get an underlying iTero material id from an alias
			iTeroMaterialId = customMaterial?.iTeroMaterialId;
			iTeroSpecId = specId ? this.getUnderlyingIteroSpecId(specId) : customMaterial?.iTeroSpecificationId;
		} else {
			iTeroMaterialId = materialId;
			iTeroSpecId = this.getUnderlyingIteroSpecId(specId ?? '');
		}

		if (iTeroMaterialId && iTeroSpecId) {
			// both original itero material and spec are identified from the custom material or spec
			return this.procedureToothEditorParser.getMaterialSpecOption(iTeroMaterialId, iTeroSpecId);
		}

		// if custom material wasn't loaded, try to use a material group from rx json
		const options = getDefaultMaterialOptions(customMaterial?.materialGroup ?? materialGroup);
		const defaultOption = options[0];

		if (iTeroSpecId !== undefined) {
			// TypeScript compiler doesn't recognize iTeroSpecId as non-undefined, so we have to use an intermediate variable as workaround.
			const iTeroSpecIdNonNull = iTeroSpecId;

			return options.find(option => option.ForSpecificationIds?.includes(iTeroSpecIdNonNull)) ?? defaultOption;
		}

		return defaultOption;
	}

	private getUnderlyingIteroSpecId(specId: number | string): number | undefined {
		if (typeof specId === 'string') {
			// the specification might have been removed or disabled. Default to trying to find it without a revision check.
			const customSpec =
				this.materialQuery.getValue().specs[specId] ?? this.materialQuery.getSpecificationWithoutRevisionCheck(specId);

			return customSpec?.iTeroSpecificationId;
		}

		return specId;
	}

	private getTranslatedCrownMappingLabel<T extends { Name: string }>(object: T, sectionName: string): T {
		return {
			...object,
			Name: this.translateService.instant(`${sectionName}.${object.Name}`)
		};
	}
}
